// src/services/kanban.ts
/**
 * Service para operaciones de Vista Kanban.
 */
import { api } from './api';
import type {
  Tablero,
  TableroCreate,
  TableroUpdate,
  TableroKanbanData,
  TipoTableroKanban,
  AgrupacionKanban,
  ColumnaConfig,
  MoverTarjetaRequest,
  MoverTarjetaResponse,
  MoverMultiplesRequest,
  ReordenarColumnaRequest,
  TarjetaKanban,
  EstadisticasTablero,
  AgrupacionInfo,
  ValorAgrupacion,
} from '../types/kanban';

export const kanbanService = {
  // ==================== TABLEROS ====================

  /**
   * Lista todos los tableros del usuario.
   * @param tipo - Filtrar por tipo de tablero
   */
  listTableros: (tipo?: TipoTableroKanban) => {
    const searchParams = new URLSearchParams();
    if (tipo) searchParams.set('tipo', tipo);
    const query = searchParams.toString();
    return api.get<Tablero[]>(`/kanban/tableros${query ? `?${query}` : ''}`);
  },

  /**
   * Crea un nuevo tablero kanban.
   */
  createTablero: (data: TableroCreate) =>
    api.post<Tablero>('/kanban/tableros', data),

  /**
   * Obtiene el tablero default de un tipo con sus datos.
   * @param tipo - Tipo de tablero (TICKETS, ACTIVIDADES, etc.)
   */
  getTableroDefault: (tipo: TipoTableroKanban) =>
    api.get<TableroKanbanData>(`/kanban/tableros/default/${tipo}`),

  /**
   * Obtiene un tablero específico con todos sus datos.
   * @param id - ID del tablero
   */
  getTablero: (id: string) =>
    api.get<TableroKanbanData>(`/kanban/tableros/${id}`),

  /**
   * Actualiza un tablero existente.
   */
  updateTablero: (id: string, data: TableroUpdate) =>
    api.put<Tablero>(`/kanban/tableros/${id}`, data),

  /**
   * Elimina un tablero.
   */
  deleteTablero: (id: string) =>
    api.delete<{ message: string }>(`/kanban/tableros/${id}`),

  /**
   * Duplica un tablero existente.
   * @param id - ID del tablero a duplicar
   * @param nombre - Nombre para el nuevo tablero
   */
  duplicarTablero: (id: string, nombre: string) => {
    const searchParams = new URLSearchParams();
    searchParams.set('nombre', nombre);
    return api.post<Tablero>(`/kanban/tableros/${id}/duplicar?${searchParams.toString()}`);
  },

  /**
   * Establece un tablero como el default del usuario para su tipo.
   * @param id - ID del tablero
   */
  setTableroDefault: (id: string) =>
    api.post<{ message: string }>(`/kanban/tableros/${id}/default`),

  // ==================== MOVIMIENTO ====================

  /**
   * Mueve una tarjeta a una nueva columna/posición.
   * @param tableroId - ID del tablero
   * @param data - Datos del movimiento
   */
  moverTarjeta: (tableroId: string, data: MoverTarjetaRequest) =>
    api.post<MoverTarjetaResponse>(`/kanban/tableros/${tableroId}/mover`, data),

  /**
   * Mueve múltiples tarjetas a la vez.
   * @param tableroId - ID del tablero
   * @param data - Lista de movimientos
   */
  moverMultiples: (tableroId: string, data: MoverMultiplesRequest) =>
    api.post<MoverTarjetaResponse[]>(`/kanban/tableros/${tableroId}/mover-multiples`, data),

  /**
   * Reordena las tarjetas dentro de una columna.
   * @param tableroId - ID del tablero
   * @param data - Datos de reordenamiento
   */
  reordenarColumna: (tableroId: string, data: ReordenarColumnaRequest) =>
    api.post<TarjetaKanban[]>(`/kanban/tableros/${tableroId}/reordenar`, data),

  // ==================== COLUMNAS ====================

  /**
   * Actualiza la configuración de una columna.
   * @param tableroId - ID del tablero
   * @param columna - Valor de la columna
   * @param config - Nueva configuración
   */
  updateColumna: (tableroId: string, columna: string, config: ColumnaConfig) =>
    api.put<ColumnaConfig>(`/kanban/tableros/${tableroId}/columnas/${columna}`, config),

  /**
   * Actualiza la configuración de una columna (alias).
   * @param tableroId - ID del tablero
   * @param columna - Valor de la columna
   * @param config - Nueva configuración (puede ser parcial)
   */
  actualizarColumna: (tableroId: string, columna: string, config: Partial<ColumnaConfig>) =>
    api.put<ColumnaConfig>(`/kanban/tableros/${tableroId}/columnas/${columna}`, config),

  /**
   * Colapsa o expande una columna.
   * @param tableroId - ID del tablero
   * @param columna - Valor de la columna
   * @param colapsada - Si colapsar (true) o expandir (false)
   */
  colapsarColumna: (tableroId: string, columna: string, colapsada: boolean = true) => {
    const searchParams = new URLSearchParams();
    searchParams.set('colapsada', String(colapsada));
    return api.post<{ message: string; colapsada: boolean }>(
      `/kanban/tableros/${tableroId}/columnas/${columna}/colapsar?${searchParams.toString()}`
    );
  },

  /**
   * Oculta una columna del tablero.
   * @param tableroId - ID del tablero
   * @param columna - Valor de la columna
   */
  ocultarColumna: (tableroId: string, columna: string) =>
    api.post<{ message: string }>(`/kanban/tableros/${tableroId}/columnas/${columna}/ocultar`),

  /**
   * Muestra una columna oculta.
   * @param tableroId - ID del tablero
   * @param columna - Valor de la columna
   */
  mostrarColumna: (tableroId: string, columna: string) =>
    api.post<{ message: string }>(`/kanban/tableros/${tableroId}/columnas/${columna}/mostrar`),

  /**
   * Reordena las columnas del tablero.
   * @param tableroId - ID del tablero
   * @param orden - Lista de valores de columnas en el nuevo orden
   */
  reordenarColumnas: (tableroId: string, orden: string[]) =>
    api.post<{ message: string }>(`/kanban/tableros/${tableroId}/columnas/reordenar`, orden),

  // ==================== UTILIDADES ====================

  /**
   * Obtiene las agrupaciones disponibles para un tipo de tablero.
   * @param tipo - Tipo de tablero
   */
  getAgrupaciones: (tipo: TipoTableroKanban) =>
    api.get<AgrupacionInfo[]>(`/kanban/agrupaciones/${tipo}`),

  /**
   * Obtiene los valores posibles para una agrupación.
   * @param agrupacion - Tipo de agrupación
   * @param filtros - Filtros adicionales opcionales
   */
  getValoresAgrupacion: (agrupacion: AgrupacionKanban, filtros?: Record<string, string>) => {
    const searchParams = new URLSearchParams();
    if (filtros) {
      Object.entries(filtros).forEach(([key, value]) => {
        searchParams.set(key, value);
      });
    }
    const query = searchParams.toString();
    return api.get<ValorAgrupacion[]>(`/kanban/valores-agrupacion/${agrupacion}${query ? `?${query}` : ''}`);
  },

  /**
   * Obtiene las estadísticas del tablero.
   * @param tableroId - ID del tablero
   */
  getEstadisticas: (tableroId: string) =>
    api.get<EstadisticasTablero>(`/kanban/tableros/${tableroId}/estadisticas`),
};
